import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Notice } from 'action/components/notice/notice';
import { ButtonMetadata, ButtonType } from 'components/buttons/button-types';
import { Card } from 'components/cards/card';
import { CardThemeType } from 'components/cards/card-type';
import { BasicLoader } from 'components/loaders/basic-loader';
import { SimpleLabels } from 'components/simple-label/simple-label';
import { AppContext } from 'core/context/app.context';
import { ClaimCard, ClaimOutcome, RewardStatus } from 'core/enums/rewards';
import { AnalyticsStructureStore } from 'core/stores/analytics-structure.store';
import { SanitizeHTML } from 'shared/components/html-sanitizer';
import { Util } from 'utils/common.utils';
import { _noop } from 'utils/function.utils';
import BasicButtons from './buttons/basic-buttons';
import Labels from './buttons/labels';
import LabelsAndButtons from './buttons/labels-and-buttons';
import CompetitionDescription from './competition-description/competition-description';
import RewardDescription from './reward-description/reward-description';
import RewardDetailsCardImage from './reward-details-card-image/reward-details-card-image';
import StarsChart from './stars-chart/stars-chart';
import { rewardsStore } from '../../rewards-store-instance';
import styles from './reward-details-card.module.scss';

type _void = () => void;

const getSuccessMessage = (type: ClaimCard) => {
  return type === ClaimCard.Reward
    ? { heading: 'rewards.success.claimReward.heading', text: 'rewards.success.claimReward.text' }
    : {
        heading: 'rewards.success.enterCompetition.heading',
        text: 'rewards.success.enterCompetition.text',
      };
};
const RewardDetailsCard = observer(
  ({
    isLoading,
    reward,
    type,
    onClose = _noop,
    onClaim,
    outcome = ClaimOutcome.None,
    selectOptions,
    onSelect,
    errorMsg,
    selectVal,
  }: {
    isLoading?: boolean;
    reward: any; // FIXME: improve rewards typing
    onClose?: _void;
    onClaim?: _void;
    type: ClaimCard;
    outcome?: ClaimOutcome;
    selectOptions?: number;
    onSelect?: (val: string) => void;
    errorMsg?: string | null;
    selectVal?: string;
  }) => {
    const { description, image, earnedAmount, earnStars, button, principlePkgs, achieved, status } = reward;
    const {
      header,
      label,
      text,
      details,
      endDate,
      termsAndConditionsLink,
      noOfEntries,
      value,
      actionLink,
      claimedMessage,
    } = description;
    const { t } = useTranslation();
    const { locale } = useContext(AppContext);
    const [checked, setChecked] = useState(false);
    const [selected, setSelected] = useState(false);
    const isClaimed = status === RewardStatus.Claimed;

    const handleSelect = (val: string) => {
      if (onSelect) onSelect(val);
      if (val) {
        setSelected(true);
      }
    };

    const Label = () => (
      <SimpleLabels
        color="success"
        isBold={false}
        smallText={true}
        labels={[
          {
            title: (
              <>
                <i className="icon-tick" />
                {type === ClaimCard.Premium
                  ? ` ${earnedAmount}% ${t('rewards.premium.achieved')}`
                  : ` ${t('rewards.label.achieved', { number: earnedAmount })}`}
              </>
            ),
          },
        ]}
        baseKey={`unlock_${label}`}
      />
    );

    const competitionAnalytics = () => {
      const analyticsName = 'rewardsEnterCompetitionButton';

      const overWriteKeys = {
        competitionName: header || '',
      };

      const componentAnalytics = AnalyticsStructureStore.getComponentAnalyticsAsJson(analyticsName, overWriteKeys);

      return {
        analyticsComponentName: componentAnalytics['component-name'],
        customAnalytics: componentAnalytics,
      };
    };

    const rewardButtonTitle = () => {
      const hasActionText = actionLink?.text && actionLink.text?.length > 1;

      if (hasActionText) {
        return actionLink.text;
      }

      return isClaimed ? t('buttons.redeem') : t('buttons.claim');
    };

    const buttonAnalytics = () => {
      return {
        analyticsTitle: `${rewardButtonTitle()}`,
        productName: reward?.name,
        analyticsTargetUrl: `${Util.BaseLocation}/#/rewards/available-rewards`,
      } as Partial<ButtonMetadata>;
    };

    return (
      <div>
        {
          {
            [ClaimOutcome.None]: (
              <Card backgroundType={CardThemeType.Light}>
                <BasicLoader isLoading={!!isLoading}>
                  <div className="d-flex flex-wrap flex-sm-nowrap justify-content-center justify-content-sm-start">
                    <RewardDetailsCardImage
                      image={image}
                      endDate={endDate}
                      type={type}
                      noOfEntries={noOfEntries}
                      achieved={achieved}
                    />
                    <div className="flex-grow-1 d-flex flex-column cust-ml-sm-4">
                      <p className={`text-large m-0 cust-pb-1 ${styles.mobileHeader}`}>{header?.replace(/_/g, ' ')}</p>
                      {
                        {
                          [ClaimCard.Reward]: (
                            <RewardDescription
                              endDate={endDate}
                              label={label}
                              isClaimed={isClaimed}
                              value={isClaimed ? value : null}
                              bodyText={details}
                              claimedMessage={claimedMessage}
                              disabled={!reward.isActive || isClaimed}
                              termsAndConditionsLink={termsAndConditionsLink}
                              onChecked={() => setChecked((checked) => !checked)}
                              checked={checked}
                            />
                          ),
                          [ClaimCard.PremiumExperience]: (
                            <RewardDescription
                              endDate={endDate}
                              label={label}
                              isClaimed={isClaimed}
                              value={isClaimed ? value : null}
                              bodyText={details}
                              claimedMessage={claimedMessage}
                              disabled={!reward.isActive || isClaimed}
                              termsAndConditionsLink={termsAndConditionsLink}
                              onChecked={() => setChecked((checked) => !checked)}
                              checked={checked}
                            />
                          ),
                          [ClaimCard.Premium]: (
                            <div className="text-regular text-dark-60 cust-pt-1 cust-mb-2">
                              <SanitizeHTML html={`${details}`} />
                            </div>
                          ),
                          [ClaimCard.Competition]: (
                            <CompetitionDescription
                              label={label}
                              details={details}
                              endDate={endDate}
                              termsAndConditionsLink={termsAndConditionsLink}
                              selectOptions={selectOptions || 0}
                              onSelect={handleSelect}
                              selectVal={selectVal}
                              disabled={!reward.isActive}
                              onChecked={() => setChecked((checked) => !checked)}
                              checked={checked}
                            />
                          ),
                          [ClaimCard.Earn]: (
                            <>
                              <div className="text-regular text-dark-60 cust-pt-1 cust-mb-2">
                                <SanitizeHTML html={`${details}`} />
                              </div>
                              {/* TODO: Replace with content from Umbraco */}
                              <StarsChart packages={principlePkgs} />
                            </>
                          ),
                          [ClaimCard.Earned]: (
                            <div className="text-regular text-dark-60 cust-pt-1 cust-mb-2">
                              <SanitizeHTML html={`${details}`} />
                            </div>
                          ),
                        }[type]
                      }
                      {
                        {
                          [ClaimCard.Reward]: (
                            <BasicButtons
                              onClaim={onClaim}
                              onClose={onClose}
                              disabled={
                                (!checked && termsAndConditionsLink?.url && termsAndConditionsLink?.url?.length > 1) ||
                                !reward.isActive
                              }
                              hideClaim={isClaimed}
                              buttonTitle={rewardButtonTitle()}
                              analyticsComponentName="rewardsProductButton"
                              analyticsAreaName="reward detail"
                              additionalMetaProperties={buttonAnalytics()}
                            />
                          ),
                          [ClaimCard.PremiumExperience]: (
                            <BasicButtons
                              onClaim={onClaim}
                              onClose={onClose}
                              disabled={
                                (!checked && termsAndConditionsLink?.url && termsAndConditionsLink?.url?.length > 1) ||
                                !reward.isActive
                              }
                              hideClaim={isClaimed}
                              buttonTitle={t('buttons.claim')}
                              analyticsComponentName="rewardsButton"
                              analyticsAreaName="reward premium experience"
                            />
                          ),
                          [ClaimCard.Competition]: (
                            <BasicButtons
                              onClaim={onClaim}
                              onClose={onClose}
                              disabled={
                                (!checked && termsAndConditionsLink?.url && termsAndConditionsLink?.url?.length > 1) ||
                                !reward.isActive ||
                                !selected
                              }
                              hideClaim={isClaimed}
                              buttonTitle={
                                actionLink?.text && actionLink.text?.length > 1
                                  ? actionLink.text
                                  : t('buttons.enterCompetition')
                              }
                              {...competitionAnalytics()}
                            />
                          ),
                          [ClaimCard.Premium]: reward.achieved ? (
                            <div className="d-flex justify-content-between align-items-baseline flex-wrap flex-sm-nowrap">
                              <p className="text-label">{text}</p>
                              <Label />
                            </div>
                          ) : (
                            <LabelsAndButtons
                              earnedAmount={earnedAmount}
                              button={{ text: button.title }}
                              onClaim={() => window.open(`${button.url}`.replace('{CULTURE}', locale), button.target)}
                              text={text}
                              analyticsComponentName="rewardsButton"
                              analyticsAreaName="reward detail"
                            />
                          ),
                          [ClaimCard.Earn]: (
                            <LabelsAndButtons
                              earnStars={earnStars}
                              button={{ text: t('buttons.getStarted') }}
                              onClaim={() => window.open(button.url, button.target)}
                              text={text}
                              analyticsComponentName="rewardsProductButton"
                              analyticsAreaName="reward detail"
                              additionalMetaProperties={{
                                areaName: 'Earn More Stars',
                                productName: reward?.description?.text,
                                analyticsTargetUrl: `${Util.BaseUrl}${button.url}`,
                              }}
                            />
                          ),
                          [ClaimCard.Earned]: (
                            <Labels
                              label={() => <Label />}
                              earnStars={earnStars}
                              earnedAmount={earnedAmount}
                              text={text}
                            />
                          ),
                        }[type]
                      }
                    </div>
                  </div>
                </BasicLoader>
              </Card>
            ),
            [ClaimOutcome.Success]: (
              <Notice
                padding
                closeOverlay={() => {
                  onClose();
                  rewardsStore.changeIsInit(false);
                }}
                heading={t(getSuccessMessage(type).heading)}
                additionalText={t(getSuccessMessage(type).text)}
                success
                analyticsComponentName="rewardsProductButton"
                analyticsAreaName={`Available rewards_Well done`}
                button={{
                  title: 'buttons.close',
                  name: 'close',
                  type: ButtonType.Primary,
                  analyticsProductName: reward?.name,
                  analyticsTargetUrl: `${Util.BaseLocation}/#/rewards/available-rewards`,
                }}
              />
            ),
            [ClaimOutcome.Fail]: (
              <Notice
                padding
                closeOverlay={onClose}
                heading={t('rewards.error.header')}
                additionalText={errorMsg}
                analyticsComponentName="rewardsProductButton"
                analyticsAreaName={`Available rewards_${t('rewards.error.header')}`}
                button={{
                  title: 'buttons.close',
                  name: 'close',
                  type: ButtonType.Primary,
                  analyticsProductName: reward?.name,
                  analyticsTargetUrl: `${Util.BaseLocation}/#/rewards/available-rewards`,
                }}
              />
            ),
          }[outcome]
        }
      </div>
    );
  }
);

export default RewardDetailsCard;